import React, { useState, useEffect, useRef } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Drawer } from 'quantux-ui';
import { blue, gray, salmon } from '../../theme';
import { base64ToBlob, equal } from '../../utils';

const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const STROKE_COLOR = '#0080FF';
const MODES = {
  draw: 'draw',
  erase: 'erase'
};

const DocWriter = (props) => {
  const [activeMode, setActiveMode] = useState(MODES.draw);
  const [backgroundImage, setBackgroundImage] = useState(new Image());
  const [isLeftMouseDown, setIsLeftMouseDown] = useState(false);
  const [isRightMouseDown, setIsRightMouseDown] = useState(false);
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [drawings, setDrawings] = useState([]);
  // -- Relating to cursor position
  const [cursorX, setCursorX] = useState(null);
  const [cursorY, setCursorY] = useState(null);
  const [prevCursorX, setPrevCursorX] = useState(null);
  const [prevCursorY, setPrevCursorY] = useState(null);
  // --- Relating to touch
  const [singleTouch, setSingleTouch] = useState(false);
  const [doubleTouch, setDoubleTouch] = useState(false);
  const [prevTouches, setPrevTouches] = useState([null, null]);
  window.prevTouches = prevTouches;
  // Relating to drawer
  const [drawerWidth, setDrawerWidth] = useState(window.innerWidth);
  const [drawerHeight, setDrawerHeight] = useState(window.innerHeight);
  const [showLoader, setShowLoader] = useState(false);

  // Refs
  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const bgImageRef = useRef({ width: 0, height: 0 });
  const dataRef = useRef({
    prevTouches: [null, null]
  });

  // Styles
  const styles = StyleSheet.create({
    canvasContainer: {
      position: 'relative',
      width: '100vw',
      // height: '100vh'
      height: '100%',
      zIndex: '1',
      overflow: 'hidden',
      touchAction: 'none' // Add this line
    },
    floatingToolbar: {
      position: 'absolute',
      display: 'flex',
      top: '10px',
      left: '10px',
      height: '32px'
    },
    toolbarBtn: {
      height: '32px',
      width: '32px',
      color: '#4C80C0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#001824',
      borderRadius: '4px',
      ':not(.sbtn):hover': {
        color: blue[400]
      },
      ':last-child': {
        marginLeft: '8px'
      }
    },
    activeToolbarBtn: {
      color: blue[400]
    },
    bgCanvas: {
      zIndex: '-1',
      position: 'absolute',
      left: '0',
      top: '0',
      touchAction: 'none' // Add this line
    },
    drawerContainer: {
      background: blue[900],
      color: blue[200],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0',
      background: blue[700]
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      opacity: showLoader ? '0.3' : '1',
      ':hover': {
        color: !showLoader ? salmon[500] : ''
      }
    },
    toolbar: {
      height: '40px',
      background: gray[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px'
    },
    toolbarLeft: {
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    toolbarRight: {
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    toolbarIcon: {
      fontSize: '22px',
      color: gray[300],
      fill: '#96C8FA',
      cursor: 'pointer',
      ':hover': {
        fill: blue[400],
        color: blue[400]
      }
    },
    badge: {
      position: 'absolute',
      top: '18px',
      right: '0px',
      padding: '0 4px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: blue[500],
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer'
    },
    pageSelector: {
      display: 'flex',
      height: '30px',
      width: '120px',
      border: `1px solid ${gray[800]}`,
      borderRadius: '5px',
      marginLeft: '16px'
    },
    pageSelectorBtn: {
      fontSize: '12px',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: blue[800]
      // background: '#083050'
    },
    pageSelectorPageNumberDisplay: {
      width: '80x',
      background: 'black',
      color: gray[300],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: '1',
      height: '30px'
    },
    drawerContentArea: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'calc(100vh - 80px)',
      overflowY: 'hidden',
      color: gray[300],
      position: 'relative'
    }
  });

  // Effects
  useEffect(() => {
    if (bgCanvasRef.current) {
      const img = new Image();
      // img.src = 'https://quantumtrader.s3.amazonaws.com/fsdemo.png';
      img.src = props.src || 'https://quantumtrader.s3.amazonaws.com/fsdemo.png';
      img.onload = () => {
        setBackgroundImage(img);
        bgImageRef.current.width = img.width;
        bgImageRef.current.height = img.height;
        const bgCanvas = bgCanvasRef.current;
        const bgContext = bgCanvas.getContext('2d');
        bgContext.drawImage(img, 0, 0);
      };
    }
    document.addEventListener('onmousedown', (e) => { e.preventDefault() });
    document.addEventListener('ontouchstart', (e) => { e.preventDefault() });
  }, []);

  // Load previous drawings
  useEffect(() => {
    if (!equal(drawings, props.drawings)) {
      setDrawings(props.drawings);
    }
  }, [props.drawings]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const bgCanvas = bgCanvasRef.current;
    const context = canvas.getContext('2d');
    const bgContext = bgCanvas.getContext('2d');

    const handleResize = () => {
      canvas.width = document.body.clientWidth;
      canvas.height = document.body.clientHeight;
      bgCanvas.width = document.body.clientWidth;
      bgCanvas.height = document.body.clientHeight;
      redrawCanvas(context, bgContext);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scale, offsetX, offsetY, drawings, backgroundImage]);

  // Helper methods
  const toScreenX = (xTrue) => (xTrue + offsetX) * scale;

  const toScreenY = (yTrue) => (yTrue + offsetY - (80 / scale)) * scale; // updated

  const toTrueX = (xScreen) => (xScreen / scale) - offsetX;

  // const toTrueY = (yScreen) => (yScreen / scale) - offsetY;
  const toTrueY = (yScreen) => (yScreen / scale) - offsetY;  // Updated

  const isWithinBounds = (x, y) => {
    const trueX = toTrueX(x);
    const trueY = toTrueY(y);
    return trueX >= 0 && trueX <= backgroundImage.width &&
      trueY >= 0 && trueY <= backgroundImage.height;
  };

  const drawLine = (context, x0, y0, x1, y1) => {
    if (isWithinBounds(x0, y0) && isWithinBounds(x1, y1)) {
      context.beginPath();
      context.moveTo(x0, y0);
      context.lineTo(x1, y1);
      context.strokeStyle = '#000';
      context.lineWidth = 2;
      context.strokeStyle = STROKE_COLOR;
      context.stroke();
    }
  };

  const eraseLine = (context, x0, y0, x1, y1) => {
    if (isWithinBounds(x0, y0) && isWithinBounds(x1, y1)) {
      context.save();
      context.globalCompositeOperation = 'destination-out';
      context.beginPath();
      context.moveTo(x0, y0);
      context.lineTo(x1, y1);
      context.strokeStyle = 'rgba(0,0,0,1)';
      context.lineWidth = 30 * scale;
      context.stroke();
      context.restore();
    }
  };

  const redrawCanvas = (context, bgContext) => {
    const canvas = canvasRef.current;
    const bgCanvas = bgCanvasRef.current;

    bgContext.clearRect(0, 0, bgCanvas.width, bgCanvas.height);
    bgContext.drawImage(backgroundImage, toScreenX(0), toScreenY(80), backgroundImage.width * scale, backgroundImage.height * scale);

    context.clearRect(0, 0, canvas.width, canvas.height);

    drawings.forEach(line => {
      if (line.type === 'draw') {
        drawLine(context, toScreenX(line.x0), toScreenY(line.y0), toScreenX(line.x1), toScreenY(line.y1));
      } else {
        eraseLine(context, toScreenX(line.x0), toScreenY(line.y0), toScreenX(line.x1), toScreenY(line.y1));
      }
    });
  };

  const trueWidth = () => canvasRef.current.clientWidth / scale;

  const trueHeight = () => canvasRef.current.clientHeight / scale;

  // Nouse Events
  const handleMouseDown = (event) => {
    if (event.button === 0) {
      setIsLeftMouseDown(true);
      setIsRightMouseDown(false);
    }
    if (event.button === 2) {
      setIsLeftMouseDown(false);
      setIsRightMouseDown(true);
    }

    setCursorX(event.pageX);
    setCursorY(event.pageY);
    setPrevCursorX(event.pageX);
    setPrevCursorY(event.pageY);
  };

  const handleMouseMove = (event) => {
    const tmpCursorX = event.pageX;
    const tmpCursorY = event.pageY;
    const scaledX = toTrueX(tmpCursorX);
    const scaledY = toTrueY(tmpCursorY);
    const prevScaledX = toTrueX(prevCursorX);
    const prevScaledY = toTrueY(prevCursorY);

    if (isLeftMouseDown) {
      if (activeMode === MODES.draw) {
        const context = canvasRef.current.getContext('2d');
        setDrawings(prev => [...prev, { type: 'draw', x0: prevScaledX, y0: prevScaledY, x1: scaledX, y1: scaledY }]);
        context.lineCap = 'round';
        drawLine(context, prevCursorX, prevCursorY, tmpCursorX, tmpCursorY);
      } else if (activeMode === MODES.erase) {
        const context = canvasRef.current.getContext('2d');
        setDrawings(prev => [...prev, { type: 'erase', x0: prevScaledX, y0: prevScaledY, x1: scaledX, y1: scaledY }]);
        eraseLine(context, prevCursorX, prevCursorY, tmpCursorX, tmpCursorY);
      }
    }
    if (isRightMouseDown) {
      setOffsetX(prev => prev + (tmpCursorX - prevCursorX) / scale);
      setOffsetY(prev => prev + (tmpCursorY - prevCursorY) / scale);
    }

    setPrevCursorX(tmpCursorX);
    setPrevCursorY(tmpCursorY);
  };

  const handleMouseUp = () => {
    setIsLeftMouseDown(false);
    setIsRightMouseDown(false);
  };

  const handleWheel = () => {
    const deltaY = event.deltaY;
    const scaleAmount = -deltaY / 500;
    setScale(prev => Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * (1 + scaleAmount))));

    var distX = event.pageX / canvasRef.current.clientWidth;
    var distY = event.pageY / canvasRef.current.clientHeight;

    const unitsZoomedX = trueWidth() * scaleAmount;
    const unitsZoomedY = trueHeight() * scaleAmount;

    const unitsAddLeft = unitsZoomedX * distX;
    const unitsAddTop = unitsZoomedY * distY;

    setOffsetX(prev => prev - unitsAddLeft);
    setOffsetY(prev => prev - unitsAddTop);
  };

  // Touch Events
  const handleTouchStart = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    }

    if (event.touches.length === 1) {
      setSingleTouch(true);
      setDoubleTouch(false);
    }

    if (event.touches.length >= 2) {
      setSingleTouch(false);
      setDoubleTouch(true);
    }
    setPrevTouches([event.touches[0], event.touches[1]]);
  };

  const handleTouchMove = (event) => {
    if (!prevTouches[0].pageX || !prevTouches[0].pageY) {
      return;
    }
    const touch0X = event.touches[0].pageX;
    const touch0Y = event.touches[0].pageY;
    const prevTouch0X = prevTouches[0].pageX;
    const prevTouch0Y = prevTouches[0].pageY;

    const scaledX = toTrueX(touch0X);
    const scaledY = toTrueY(touch0Y);
    const prevScaledX = toTrueX(prevTouch0X);
    const prevScaledY = toTrueY(prevTouch0Y);

    if (singleTouch) {
      if (activeMode === MODES.draw) {
        setDrawings(prev => [...prev, { type: 'draw', x0: prevScaledX, y0: prevScaledY, x1: scaledX, y1: scaledY }]);
        const context = canvasRef.current.getContext('2d');
        drawLine(context, prevTouch0X, prevTouch0Y, touch0X, touch0Y);
      } else if (activeMode === MODES.erase) {
        setDrawings(prev => [...prev, { type: 'erase', x0: prevScaledX, y0: prevScaledY, x1: scaledX, y1: scaledY }]);
        const context = canvasRef.current.getContext('2d');
        eraseLine(context, prevTouch0X, prevTouch0Y, touch0X, touch0Y);
      }
    }

    if (doubleTouch) {
      const touch1X = event.touches[1].pageX;
      const touch1Y = event.touches[1].pageY;
      const prevTouch1X = prevTouches[1].pageX;
      const prevTouch1Y = prevTouches[1].pageY;

      const midX = (touch0X + touch1X) / 2;
      const midY = (touch0Y + touch1Y) / 2;
      const prevMidX = (prevTouch0X + prevTouch1X) / 2;
      const prevMidY = (prevTouch0Y + prevTouch1Y) / 2;

      const hypot = Math.sqrt(Math.pow((touch0X - touch1X), 2) + Math.pow((touch0Y - touch1Y), 2));
      const prevHypot = Math.sqrt(Math.pow((prevTouch0X - prevTouch1X), 2) + Math.pow((prevTouch0Y - prevTouch1Y), 2));

      var zoomAmount = hypot / prevHypot;
      // scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * zoomAmount));
      setScale(prev => Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * zoomAmount)));
      const scaleAmount = 1 - zoomAmount;

      const panX = midX - prevMidX;
      const panY = midY - prevMidY;

      setOffsetX(prev => prev + (panX / scale));
      setOffsetY(prev => prev + (panY / scale));

      var zoomRatioX = midX / canvasRef.current.clientWidth;
      var zoomRatioY = midY / canvasRef.current.clientHeight;

      const unitsZoomedX = trueWidth() * scaleAmount;
      const unitsZoomedY = trueHeight() * scaleAmount;

      const unitsAddLeft = unitsZoomedX * zoomRatioX;
      const unitsAddTop = unitsZoomedY * zoomRatioY;

      setOffsetX(prev => prev + unitsAddLeft);
      setOffsetY(prev => prev + unitsAddTop);
    }
    setPrevTouches([event.touches[0], event.touches[1]]);
  };

  const handleTouchEnd = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    }
    setSingleTouch(false);
    setDoubleTouch(false);
    setPrevTouches([null, null]);
  };

  const handleOnClickPencil = () => {
    setActiveMode(MODES.draw);
  };

  const handleOnClickSave = () => {
    try {
      props.onClose(props.docId, drawings);
    } catch (error) {
      console.error('Error saving the image:', error);
    }
  };

  const handleOnClickClose = () => {
    try {
      props.onClose(props.docId, drawings);
    } catch (error) {
      console.error('Error saving the image:', error);
    }
  };

  const handleOnClickEraser = () => {
    setActiveMode(MODES.erase);
  };

  return (
    <Drawer
      placement='right'
      visible
      toggleDrawer={() => props.onClose()}
      zIndex="50000"
      showMask={false}
      style={{ border: 'none' }}
      width={drawerWidth}
    >
      <div className={css(styles.drawerContainer)}>
        {/* Drawer Header */}
        <div className={css(styles.drawerHeader)}>
          <div className='fx fx__hv-center'>
            <div className='ml-2'>
              Doc Writer
            </div>
          </div>
          <i
            className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
            onClick={handleOnClickClose}
          />
        </div>
        {/* Toolbar */}
        <div className={css(styles.toolbar)}>
          {/* Toolbar left section */}
          <div className={css(styles.toolbarLeft)}>
            {/* Thumbnails drawer icon */}
            <div
              style={{
                position: 'relative',
                paddingTop: '6px'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="30"
                fill="none"
                className={css(styles.toolbarIcon)}
                onClick={() => {
                  console.log('Not implemented yet!');
                }}
              >
                <path
                  fillRule="evenodd"
                  d="M29.285 0H2.215C.99 0 0 1.258 0 2.813v24.375C0 28.741.99 30 2.215 30h27.07c1.224 0 2.215-1.258 2.215-2.813V2.813C31.5 1.258 30.51 0 29.285 0ZM12 27H3V3h9v24Zm3 0h13.5V3c0 .085-.01.142-.022.142H15.022C15.009 3.142 15 3.071 15 3v24Zm-4.5-16.5h-6v-6h6v6Zm-6 7.5h6v-6h-6v6Z"
                  clipRule="evenodd"
                />
              </svg>
              {/* Badge */}
              <div
                className={css(styles.badge)}
                onClick={() => {
                  console.log('Not implemented yet!');
                }}
              >
                10
              </div>
            </div>
            {/* Page Selector */}
            <div className={`ml-2 ${css(styles.pageSelector)}`}>
              {/* Go Back */}
              <div
                className={css(styles.pageSelectorBtn)}
                style={{
                  fontSize: '18px',
                  borderTopLeftRadius: '5px',
                  borderBottomLeftRadius: '5px',
                  color: '#5080B0'
                }}
                onClick={() => {
                  console.log('Not implemented yet!');
                }}
              >
                <i className='fa fa-minus-circle' />
              </div>
              {/* Current Page */}
              <div className={css(styles.pageSelectorPageNumberDisplay)}>
                1
              </div>
              {/* Go Forward */}
              <div
                className={css(styles.pageSelectorBtn)}
                style={{
                  color: '#5080B0',
                  fontSize: '18px',
                  borderTopRightRadius: '5px',
                  borderBottomRightRadius: '5px'
                }}
                onClick={() => {
                  console.log('Not implemented yet!');
                }}
              >
                <i className='fa fa-plus-circle' />
              </div>
            </div>
          </div>
          {/* Toolbar right section */}
          <div className={css(styles.toolbarRight)}>
            {/* <i
              className={`fa fa-floppy-o ${css(styles.toolbarBtn)}`}
              onClick={handleOnClickSave}
            /> */}
            <i
              className={`sbtn ml-2 fa fa-pencil ${css(styles.toolbarBtn, activeMode === MODES.draw && styles.activeToolbarBtn)}`}
              onClick={handleOnClickPencil}
            />
            <i
              className={`sbtn fa fa-eraser ${css(styles.toolbarBtn, activeMode === MODES.erase && styles.activeToolbarBtn)}`}
              onClick={handleOnClickEraser}
            />
          </div>
        </div>
        {/* Drawer Container */}
        <div className={css(styles.drawerContentArea)}>
          <div className={css(styles.canvasContainer)}>
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onWheel={handleWheel}
              onContextMenu={(e) => e.preventDefault()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              Your browser does not support HTML5 canvas
            </canvas>
            <canvas
              ref={bgCanvasRef}
              className={css(styles.bgCanvas)}
              src="https://quantumtrader.s3.amazonaws.com/fsdemo.png"
              alt="Background Image"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default DocWriter;
